import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Spinner,
  ProgressBar,
  Alert,
  Badge,
  Modal 

 
} from "react-bootstrap";
import Header from "../../layouts/Header";
import Footer from "../../layouts/Footer";
import axios from "axios";
import {
  CheckCircle,
  ClockHistory,
  ExclamationTriangle,
  ArrowLeft,
  Phone,
  Cash,
  Receipt,
 
  Check,
  XCircle,
  ArrowRepeat,
  Download
} from "react-bootstrap-icons";

// Configuration de l'API
const API_BASE_URL = process.env.REACT_APP_API_URL;

const AttenteValidationPage = () => {
  const { transactionId } = useParams();
  const navigate = useNavigate();
  
  const [transaction, setTransaction] = useState(null);
  const [status, setStatus] = useState("pending"); // pending, success, failed, processing
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [countdown, setCountdown] = useState(20); // Changé de 60 à 20 secondes
  const [pollingInterval, setPollingInterval] = useState(null);
  const [timeoutReached, setTimeoutReached] = useState(false);
  
  // État pour la modal de statut
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalMessage, setModalMessage] = useState("");
  const [modalVariant, setModalVariant] = useState("info");
  
  // État pour le téléchargement du reçu
  const [downloadingReceipt, setDownloadingReceipt] = useState(false);
  
  // Référence pour le timeout
  const paymentTimeoutRef = useRef(null);
  
  const getAuthToken = () => {
    return sessionStorage.getItem("authToken");
  };
  
  const getHeaders = () => {
    const token = getAuthToken();
    return {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  };
  
  // Fonction pour afficher une modal d'information
  const showStatusInfo = (title, message, variant = "info") => {
    setModalTitle(title);
    setModalMessage(message);
    setModalVariant(variant);
    setShowStatusModal(true);
  };
  
  // Fonction pour fermer la modal
  const handleCloseModal = () => {
    setShowStatusModal(false);
  };
  
  // Fonction pour télécharger le reçu
  const downloadReceipt = async () => {
    if (!transaction?.reference) {
      showStatusInfo(
        "Erreur",
        "Référence de transaction non disponible",
        "danger"
      );
      return;
    }
    
    try {
      setDownloadingReceipt(true);
      
      // Appeler l'API pour obtenir le reçu PDF
      const response = await axios.get(
        `${API_BASE_URL}/payments/${transaction.reference}/receipt`,
        { 
          headers: getHeaders(),
          responseType: 'blob' // Important pour les fichiers
        }
      );
      
      // Créer une URL pour le blob
      const url = window.URL.createObjectURL(new Blob([response.data]));
      
      // Créer un lien temporaire pour le téléchargement
      const link = document.createElement('a');
      link.href = url;
      
      // Nommer le fichier
      const fileName = `reçu-${transaction.reference}-${new Date().toISOString().split('T')[0]}.pdf`;
      link.setAttribute('download', fileName);
      
      // Déclencher le téléchargement
      document.body.appendChild(link);
      link.click();
      
      // Nettoyer
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      showStatusInfo(
        "Reçu téléchargé",
        "Le reçu a été téléchargé avec succès.",
        "success"
      );
      
    } catch (error) {
     
      
      // Si l'API ne supporte pas le téléchargement direct, afficher un message
      if (error.response?.status === 404 || error.response?.status === 501) {
        showStatusInfo(
          "Fonctionnalité non disponible",
          "Le téléchargement du reçu n'est pas encore disponible. Veuillez consulter votre reçu en ligne.",
          "warning"
        );
      } else {
        showStatusInfo(
          "Erreur de téléchargement",
          `Impossible de télécharger le reçu : ${error.response?.data?.message || error.message}`,
          "danger"
        );
      }
    } finally {
      setDownloadingReceipt(false);
    }
  };
  
  // Fonction pour générer un reçu simple en PDF (fallback)
  const generateSimpleReceipt = () => {
    // Cette fonction génère un reçu simple en PDF côté client
    // Vous pouvez utiliser une bibliothèque comme jsPDF si nécessaire
    // Pour l'instant, on va créer un reçu HTML simple
    
    const receiptContent = `
      <html>
        <head>
          <title>Reçu de paiement - ${transaction?.reference}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            .header { text-align: center; margin-bottom: 30px; }
            .title { font-size: 24px; font-weight: bold; color: #2c3e50; }
            .subtitle { font-size: 14px; color: #7f8c8d; margin-top: 5px; }
            .receipt-details { margin: 30px 0; }
            .detail-row { display: flex; justify-content: space-between; margin: 10px 0; }
            .detail-label { font-weight: bold; color: #34495e; }
            .detail-value { color: #2c3e50; }
            .amount { font-size: 20px; font-weight: bold; color: #27ae60; text-align: center; margin: 20px 0; }
            .footer { margin-top: 40px; text-align: center; color: #7f8c8d; font-size: 12px; }
            .stamp { 
              border: 2px solid #27ae60; 
              color: #27ae60; 
              padding: 10px; 
              display: inline-block; 
              transform: rotate(-5deg);
              font-weight: bold;
              margin: 20px auto;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="title">REÇU DE PAIEMENT</div>
            <div class="subtitle">Minerve Technologies</div>
            <div class="subtitle">${new Date().toLocaleDateString('fr-FR')} - ${new Date().toLocaleTimeString('fr-FR')}</div>
          </div>
          
          <div class="receipt-details">
            <div class="detail-row">
              <span class="detail-label">Référence:</span>
              <span class="detail-value">${transaction?.reference || 'N/A'}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Date:</span>
              <span class="detail-value">${new Date().toLocaleDateString('fr-FR')}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Heure:</span>
              <span class="detail-value">${new Date().toLocaleTimeString('fr-FR')}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Méthode de paiement:</span>
              <span class="detail-value">${transaction?.paymentMethod === "mobile_money" ? "Mobile Money" : transaction?.paymentMethod || 'N/A'}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Réseau:</span>
              <span class="detail-value">${transaction?.networkName || 'N/A'}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Téléphone:</span>
              <span class="detail-value">${transaction?.phoneNumber || 'N/A'}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Statut:</span>
              <span class="detail-value" style="color: #27ae60; font-weight: bold;">PAYÉ</span>
            </div>
          </div>
          
          <div class="amount">
            ${formatCurrency(transaction?.amount)}
          </div>
          
          <div style="text-align: center;">
            <div class="stamp">PAYÉ</div>
          </div>
          
          <div class="footer">
            <p>Ce document fait foi de paiement.</p>
            <p>Conservez ce reçu pour vos archives.</p>
            <p>Merci pour votre confiance.</p>
          </div>
        </body>
      </html>
    `;
    
    // Ouvrir dans une nouvelle fenêtre pour impression
    const receiptWindow = window.open('', '_blank');
    receiptWindow.document.write(receiptContent);
    receiptWindow.document.close();
    
    // Attendre que le contenu soit chargé avant d'imprimer
    receiptWindow.onload = () => {
      receiptWindow.print();
    };
  };
  
  // Fonction combinée pour télécharger le reçu
  const handleDownloadReceipt = async () => {
    // Essayer d'abord le téléchargement via l'API
    await downloadReceipt();
  };
  
  // Fonction pour arrêter tous les intervalles et timeouts
  const cleanupIntervals = () => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
    if (paymentTimeoutRef.current) {
      clearTimeout(paymentTimeoutRef.current);
      paymentTimeoutRef.current = null;
    }
  };
  
  // Fonction pour marquer le paiement comme échoué
  const markPaymentAsFailed = (reason = "Délai d'attente dépassé") => {
    setStatus("failed");
    setProgress(100); // Barre à 100% mais en rouge pour échec
    
    // Stocker l'échec
    const failureResult = {
      success: false,
      transactionId: transaction?.reference,
      amount: transaction?.amount,
      timestamp: new Date().toISOString(),
      reason: reason,
      timeout: true
    };
    
    localStorage.setItem('lastPaymentResult', JSON.stringify(failureResult));
    localStorage.removeItem('pendingTransaction');
    
    // Nettoyer les intervalles
    cleanupIntervals();
    

  };
  
  // Fonction de polling pour vérifier le statut
  const startPolling = (txId) => {
    const interval = setInterval(async () => {
      try {
       
        const response = await axios.get(
          `${API_BASE_URL}/payments/${txId}`,
          { headers: getHeaders() }
        );
        
        const data = response.data;
       
        
        if (data.code === 200 || response.status === 200) {
          const paymentStatus = data.paiement?.statut || data.status || data.statut;
          
          if (paymentStatus === "completed" || paymentStatus === "success" || paymentStatus === "payé") {
            setStatus("success");
            setProgress(100);
            
            // Stocker le résultat
            localStorage.setItem('lastPaymentResult', JSON.stringify(data));
            localStorage.removeItem('pendingTransaction');
            
            // Nettoyer les intervalles
            cleanupIntervals();
            
          
            
          } else if (paymentStatus === "failed" || paymentStatus === "rejected" || paymentStatus === "échoué" || paymentStatus === "annulé") {
            markPaymentAsFailed(`Statut API: ${paymentStatus}`);
          } else {
            // Statut toujours en attente (pending, en_attente, etc.)
         
          }
        } else {
          console.warn(`Code de réponse non 200: ${data.code || response.status}`);
        }
      } catch (error) {

        
        // Si l'erreur est 404, le paiement n'existe pas
        if (error.response?.status === 404) {
          markPaymentAsFailed("Transaction non trouvée (404)");
        }
      }
    }, 3000); // Vérifier toutes les 3 secondes (plus fréquent)
    
    setPollingInterval(interval);
  };
  
  // Charger les données de transaction
  useEffect(() => {
    const loadTransaction = () => {
      const storedTransaction = localStorage.getItem('pendingTransaction');
      if (storedTransaction) {
        const transactionData = JSON.parse(storedTransaction);
        setTransaction(transactionData);
        setLoading(false);
        
        // Si c'est une démo, simuler le traitement
        if (transactionData.isDemo) {
          simulatePaymentProcessing();
        } else {
          // Commencer le polling pour vérifier le statut
          startPolling(transactionData.reference);
          
          // Déclencher le timeout de 20 secondes
          paymentTimeoutRef.current = setTimeout(() => {
            if (status === "pending") {
              setTimeoutReached(true);
              markPaymentAsFailed();
            }
          }, 20000); // 20 secondes
        }
      } else {
        // Rediriger si pas de données
        setTimeout(() => {
          navigate("/echeances-tax/"+localStorage.getItem('taxeID')+"/"+localStorage.getItem('contribuableId'));
        }, 2000);
      }
    };
    
    loadTransaction();
    
    // Animation de progression
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        // Ne pas dépasser 100%
        if (prev >= 100) return 100;
        // Augmenter de 1% toutes les 400ms pour atteindre 100% en 40 secondes
        // mais sera arrêté par le timeout de 20 secondes
        return prev + 0.5;
      });
    }, 400);
    
    // Compte à rebours de 20 secondes
    const countdownInterval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 0) {
          clearInterval(countdownInterval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    // Nettoyage à la destruction du composant
    return () => {
      clearInterval(progressInterval);
      clearInterval(countdownInterval);
      cleanupIntervals();
    };
  }, [transactionId]);
  
  // Simulation pour les démos
  const simulatePaymentProcessing = () => {
    setTimeout(() => {
      setProgress(50);
      setStatus("processing");
      
      setTimeout(() => {
        setProgress(100);
        setStatus("success");
        
        // Simuler un résultat de paiement
        const mockResult = {
          success: true,
          transactionId: transactionId,
          amount: transaction?.amount,
          timestamp: new Date().toISOString()
        };
        
        localStorage.setItem('lastPaymentResult', JSON.stringify(mockResult));
        localStorage.removeItem('pendingTransaction');
      }, 5000);
    }, 3000);
  };
  
  // Vérification manuelle du statut
  const handleManualCheck = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}/payments/${transaction?.reference}`,
        { headers: getHeaders() }
      );
      
      const data = response.data;
      const paymentStatus = data.paiement?.statut || data.status || data.statut;
      
      // Déterminer le style et le message selon le statut
      let statusDisplay = paymentStatus || 'Inconnu';
      let variant = "info";
      let title = "Statut du paiement";
      
      switch(paymentStatus) {
        case "payé":
        case "success":
        case "completed":
          statusDisplay = "✅ Payé";
          variant = "success";
          title = "Paiement confirmé";
          break;
        case "failed":
        case "rejected":
        case "échoué":
          statusDisplay = "❌ Échoué";
          variant = "danger";
          title = "Paiement échoué";
          break;
        case "pending":
        case "en_attente":
          statusDisplay = "⏳ En attente";
          variant = "warning";
          title = "Paiement en attente";
          break;
        case "annulé":
          statusDisplay = "❌ Annulé";
          variant = "danger";
          title = "Paiement annulé";
          break;
        default:
          statusDisplay = `📄 ${paymentStatus}`;
      }
      
      // Afficher la modal avec le statut
      showStatusInfo(
        title,
        `Le statut actuel de votre paiement est : ${statusDisplay}`,
        variant
      );
      
      // Mettre à jour l'état si le statut a changé
      if (paymentStatus === "payé" || paymentStatus === "success" || paymentStatus === "completed") {
        setStatus("success");
        setProgress(100);
        cleanupIntervals();
      }
      
    } catch (error) {
  
      
      // Afficher l'erreur dans une modal
      showStatusInfo(
        "Erreur de vérification",
        `Impossible de vérifier le statut : ${error.response?.data?.message || error.message}`,
        "danger"
      );
    } finally {
      setLoading(false);
    }
  };
  
  // Réessayer le paiement
  const handleRetry = () => {
    cleanupIntervals();
    if (transaction) {
      navigate(-1); // Retourner à la page de paiement
    }
  };
  
  // Voir le reçu
  const handleViewReceipt = () => {
    if (transaction) {
      navigate(`/receipt/${transaction.transactionId}`);
    }
  };
  
  // Retour aux échéances
  const handleBackToEcheances = () => {
    navigate(`/echeances/${transaction?.taxId}/${transaction?.contribuableId}`);
  };
  
  // Formater la monnaie
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };
  
  if (loading) {
    return (
      <>
        <Header />
        <div className="main main-app p-3 p-lg-4">
        <Container className="d-flex justify-content-center align-items-center min-vh-100">
          <div className="text-center">
            <Spinner animation="border" variant="primary" size="lg" />
            <h4 className="mt-3">Chargement de la transaction...</h4>
          </div>
        </Container>
        </div>
        <Footer />
      </>
    );
  }
  
  return (
    <>
      <Header />
      
      <div className="main main-app p-3 p-lg-4">
        <Container>
          {/* Bouton retour */}
          <Button 
            variant="light" 
            onClick={() => navigate(-1)}
            className="mb-4"
          >
            <ArrowLeft className="me-2" />
            Retour
          </Button>
          
          <Row className="justify-content-center">
            <Col lg={8}>
              <Card className="border-0 shadow-lg">
                <Card.Body className="p-4">
                  {/* En-tête */}
                  <div className="text-center mb-5">
                    <div className={`status-icon mb-4 ${
                      status === "success" ? "text-success" :
                      status === "failed" ? "text-danger" :
                      status === "processing" ? "text-warning" :
                      "text-primary"
                    }`}>
                      {status === "success" ? (
                        <CheckCircle size={80} />
                      ) : status === "failed" ? (
                        <XCircle size={80} />
                      ) : status === "processing" ? (
                        <ClockHistory size={80} />
                      ) : (
                        <ClockHistory size={80} />
                      )}
                    </div>
                    
                    <h2 className="mb-3">
                      {status === "success" ? "✅ Paiement Réussi !" :
                       status === "failed" ? "❌ Paiement Échoué" :
                       status === "processing" ? "⏳ Traitement en cours..." :
                       "⏳ En attente de validation"}
                    </h2>
                    
                    <p className="text-muted mb-0">
                      {status === "success" ? "Votre paiement a été validé avec succès." :
                       status === "failed" ? 
                       (timeoutReached 
                         ? "Le délai de 20 secondes est écoulé sans confirmation. Veuillez réessayer." 
                         : "Une erreur est survenue lors du traitement.") :
                       status === "processing" ? "Vérification du paiement en cours..." :
                       `Veuillez valider le paiement sur votre téléphone. Temps restant : ${countdown}s`}
                    </p>
                  </div>
                  
                  {/* Barre de progression */}
                  <div className="mb-5">
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Progression</span>
                      <span className="fw-bold">{Math.min(progress, 100).toFixed(0)}%</span>
                    </div>
                    <ProgressBar 
                      now={progress} 
                      variant={
                        status === "success" ? "success" :
                        status === "failed" ? "danger" :
                        status === "processing" ? "warning" :
                        "primary"
                      }
                      animated={status === "pending" || status === "processing"}
                      className="mb-3"
                      style={{ height: '10px' }}
                    />
                    <div className="d-flex justify-content-between small text-muted">
                      <span>⏳ Initié</span>
                      <span>🔄 En cours ({countdown}s)</span>
                      <span>{status === "failed" ? "❌ Échoué" : "✅ Terminé"}</span>
                    </div>
                  </div>
                  
                  {/* Détails de la transaction */}
                  <Card className="border mb-5">
                    <Card.Body className="p-4">
                      <h5 className="mb-4">
                        <Receipt className="me-2" />
                        Détails de la transaction
                      </h5>
                      
                      <Row className="g-3">
                        <Col md={6}>
                          <div className="d-flex justify-content-between mb-3">
                            <span className="text-muted">Référence:</span>
                            <Badge bg="info" className="fs-6">
                              {transaction?.reference}
                            </Badge>
                          </div>
                          <div className="d-flex justify-content-between mb-3">
                            <span className="text-muted">Montant:</span>
                            <span className="fw-bold">{formatCurrency(transaction?.amount)}</span>
                          </div>
                          <div className="d-flex justify-content-between mb-3">
                            <span className="text-muted">Méthode:</span>
                            <span className="fw-bold">
                              {transaction?.paymentMethod === "mobile_money" ? (
                                <>
                                  <Phone className="me-1" />
                                  Mobile Money
                                </>
                              ) : (
                                <>
                                  <Cash className="me-1" />
                                  {transaction?.paymentMethod}
                                </>
                              )}
                            </span>
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="d-flex justify-content-between mb-3">
                            <span className="text-muted">Réseau:</span>
                            <span className="fw-bold">
                              {transaction?.networkName || "Non spécifié"}
                            </span>
                          </div>
                          <div className="d-flex justify-content-between mb-3">
                            <span className="text-muted">Téléphone:</span>
                            <span className="fw-bold">
                              {transaction?.phoneNumber || "Non spécifié"}
                            </span>
                          </div>
                          <div className="d-flex justify-content-between">
                            <span className="text-muted">Statut:</span>
                            <Badge bg={
                              status === "success" ? "success" :
                              status === "failed" ? "danger" :
                              status === "processing" ? "warning" :
                              "info"
                            }>
                              {status === "success" ? "✅ Confirmé" :
                               status === "failed" ? (timeoutReached ? "⌛ Timeout" : "❌ Échoué") :
                               status === "processing" ? "🔄 En traitement" :
                               `⏳ En attente (${countdown}s)`}
                            </Badge>
                          </div>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                  
                  {/* Actions */}
                  <div className="text-center">
                    {status === "success" ? (
                      <div className="d-flex justify-content-center gap-3 flex-wrap">
                        <Button 
                          variant="success" 
                          size="lg"
                          onClick={handleViewReceipt}
                          className="px-5 mb-2"
                        >
                          <Receipt className="me-2" />
                          Voir le reçu
                        </Button>
                        <Button 
                          variant="primary" 
                          size="lg"
                          onClick={handleDownloadReceipt}
                          disabled={downloadingReceipt}
                          className="px-5 mb-2"
                        >
                          {downloadingReceipt ? (
                            <>
                              <Spinner animation="border" size="sm" className="me-2" />
                              Téléchargement...
                            </>
                          ) : (
                            <>
                              <Download className="me-2" />
                              Télécharger le reçu
                            </>
                          )}
                        </Button>
                        <Button 
                          variant="outline-primary" 
                          size="lg"
                          onClick={handleBackToEcheances}
                          className="px-5 mb-2"
                        >
                          <ArrowLeft className="me-2" />
                          Retour aux échéances
                        </Button>
                      </div>
                    ) : status === "failed" ? (
                      <div className="d-flex justify-content-center gap-3">
                        <Button 
                          variant="danger" 
                          size="lg"
                          onClick={handleRetry}
                          className="px-5"
                        >
                          <ArrowRepeat className="me-2" />
                          Réessayer le paiement
                        </Button>
                        <Button 
                          variant="outline-secondary" 
                          size="lg"
                          onClick={handleManualCheck}
                          className="px-5"
                        >
                          <Check className="me-2" />
                          Vérifier manuellement
                        </Button>
                      </div>
                    ) : (
                      <div className="d-flex flex-column align-items-center gap-3">
                        <div className="d-flex justify-content-center gap-3 mb-2">
                          <Button 
                            variant="primary" 
                            size="lg"
                            disabled
                            className="px-5"
                          >
                            <ClockHistory className="me-2" />
                            En attente... ({countdown}s)
                          </Button>
                          <Button 
                            variant="outline-secondary" 
                            size="lg"
                            onClick={handleRetry}
                            className="px-5"
                          >
                            <ArrowRepeat className="me-2" />
                            Annuler
                          </Button>
                        </div>
                        
                        {/* Bouton de vérification manuelle */}
                        <Button 
                          variant="info" 
                          size="md"
                          onClick={handleManualCheck}
                          className="px-4"
                          disabled={loading}
                        >
                          {loading ? (
                            <>
                              <Spinner animation="border" size="sm" className="me-2" />
                              Vérification...
                            </>
                          ) : (
                            <>
                              <Check className="me-2" />
                              Vérifier manuellement
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                  
                  {/* Message d'information */}
                  {status === "pending" && (
                    <Alert variant={
                      countdown <= 5 ? "warning" : "light"
                    } className="mt-4 text-center">
                      <small className={
                        countdown <= 5 ? "text-warning fw-bold" : "text-muted"
                      }>
                        <ClockHistory className="me-1" />
                        {countdown <= 5 
                          ? `Attention ! Plus que ${countdown} secondes pour valider le paiement.` 
                          : `Cette page se mettra à jour automatiquement. Temps restant: ${countdown}s`}
                      </small>
                    </Alert>
                  )}
                  
                  {/* Message de timeout */}
                  {timeoutReached && (
                    <Alert variant="danger" className="mt-4">
                      <div className="d-flex align-items-center">
                        <ExclamationTriangle className="me-2 flex-shrink-0" />
                        <div>
                          <strong>Délai dépassé :</strong> Le paiement n'a pas été confirmé dans les 20 secondes.
                          Cela peut être dû à :
                          <ul className="mb-0 mt-2">
                            <li>Une connexion internet instable</li>
                            <li>Une non-validation sur votre téléphone</li>
                            <li>Un problème temporaire du réseau mobile money</li>
                          </ul>
                        </div>
                      </div>
                    </Alert>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      
      <Footer />
      
      {/* Modal pour afficher le statut */}
      <Modal show={showStatusModal} onHide={handleCloseModal} centered>
        <Modal.Header 
          closeButton 
          className={`bg-${modalVariant} text-white`}
        >
          <Modal.Title>
            {modalVariant === "success" ? "✅ " : 
             modalVariant === "danger" ? "❌ " :
             modalVariant === "warning" ? "⚠️ " : "ℹ️ "}
            {modalTitle}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center py-3">
            <div className={`display-6 text-${modalVariant} mb-3`}>
              {modalVariant === "success" ? <CheckCircle size={60} /> :
               modalVariant === "danger" ? <XCircle size={60} /> :
               modalVariant === "warning" ? <ExclamationTriangle size={60} /> :
               <CheckCircle size={60} />}
            </div>
            <p className="lead">{modalMessage}</p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Fermer
          </Button>
          {modalVariant === "success" && (
            <Button variant="success" onClick={() => {
              handleCloseModal();
              setStatus("success");
              setProgress(100);
              cleanupIntervals();
            }}>
              Mettre à jour l'affichage
            </Button>
          )}
          {modalVariant === "danger" && (
            <Button variant="danger" onClick={handleRetry}>
              <ArrowRepeat className="me-2" />
              Réessayer
            </Button>
          )}
        </Modal.Footer>
      </Modal>
      
      {/* Styles CSS */}
      <style>{`
        .status-icon {
          animation: ${status === "pending" || status === "processing" ? "pulse 2s infinite" : "none"};
        }
        
        @keyframes pulse {
          0% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.05); }
          100% { opacity: 1; transform: scale(1); }
        }
        
        .progress-bar {
          border-radius: 10px;
          transition: width 0.4s ease;
        }
        
        .card {
          border-radius: 15px;
        }
        
        .modal-header.bg-success,
        .modal-header.bg-danger,
        .modal-header.bg-warning,
        .modal-header.bg-info {
          border-bottom: none;
        }
        
        @media (max-width: 768px) {
          .d-flex.justify-content-center.gap-3 {
            flex-direction: column;
            gap: 1rem !important;
          }
          .flex-wrap {
            flex-direction: column;
            align-items: center;
          }
        }
      `}</style>
    </>
  );
};

export default AttenteValidationPage;